import { cn } from "../../lib/utils"
import type { MentionCandidate } from "./use-mention-compose"

/**
 * The @-mention dropdown, anchored above the composer. Selection uses
 * `onMouseDown` + preventDefault so the click doesn't blur the textarea
 * before the insert runs.
 */
export function MentionPicker({
	id,
	label,
	candidates,
	activeIndex,
	onSelect,
	onHover,
}: {
	id: string
	label: string
	candidates: MentionCandidate[]
	activeIndex: number
	onSelect: (c: MentionCandidate) => void
	onHover: (index: number) => void
}) {
	return (
		<div
			id={id}
			role="listbox"
			aria-label={label}
			className="absolute bottom-full left-3 mb-1 w-64 max-h-56 overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-lg z-40 py-1 text-sm"
		>
			{candidates.map((c, i) => (
				<div key={c.key} role="presentation">
					<button
						id={`${id}-option-${i}`}
						type="button"
						role="option"
						aria-selected={i === activeIndex}
						onMouseDown={(e) => {
							e.preventDefault()
							onSelect(c)
						}}
						onMouseEnter={() => onHover(i)}
						className={cn(
							"w-full text-left px-3 py-1.5 flex items-center justify-between gap-2 cursor-pointer",
							i === activeIndex
								? "bg-accent text-foreground"
								: "hover:bg-accent/50",
							"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-popover",
						)}
					>
						<span className="truncate">{c.displayName}</span>
						{c.kind === "ai" ? (
							<span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
								AI
							</span>
						) : null}
					</button>
				</div>
			))}
		</div>
	)
}
